// Articles by Kalah K. Singam — full-text on-site versions of LinkedIn posts.
// Each article is a list of typed content blocks for easy rendering.
//
// Block types supported by ArticlePage:
//   { type: 'p',  text: string }
//   { type: 'list', items: string[] }
//   { type: 'pullquote', text: string }
//   { type: 'h2', text: string }

export const articles = [
  {
    slug: 'please-teach-me-differently',
    title: 'Please Teach Me Differently',
    subtitle:
      'A reflection on what children really mean when they say "I can\'t read."',
    excerpt:
      'After 20+ years in early childhood education, one truth keeps coming back to me: a child who struggles to read is often misunderstood.',
    date: '2026-02-14',
    dateDisplay: '14 February 2026',
    readMinutes: 3,
    heroImage:
      'https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/nneficdq_image.png',
    heroAlt:
      'A young boy concentrating quietly while exploring a board book at his table.',
    tags: ['Early Childhood', 'Child Development', 'Reading'],
    body: [
      {
        type: 'p',
        text: 'After more than 20 years in early childhood education, one thing continues to stay with me: a child who struggles to read is often misunderstood.'
      },
      {
        type: 'p',
        text: 'Many people assume the child is inattentive, distracted, or not trying hard enough. In many situations, the child simply has not been taught in a way that matches how young children learn best.'
      },
      {
        type: 'p',
        text: 'Over the years, I have seen children transform when reading is taught with patience, structure, movement, sound awareness, and encouragement.'
      },
      {
        type: 'h2',
        text: 'Reading is more than memorising words'
      },
      { type: 'p', text: 'Reading is not just about memorising words. It is about:' },
      {
        type: 'list',
        items: [
          'confidence',
          'connection',
          'language',
          'cognitive development',
          'emotional growth'
        ]
      },
      {
        type: 'pullquote',
        text: 'When a child says, "I can\'t read," what they may really be saying is: "Please teach me differently."'
      },
      {
        type: 'p',
        text: 'As educators and parents, we sometimes need to pause and ask ourselves: are we teaching the child, or are we expecting the child to adapt to our teaching?'
      },
      {
        type: 'p',
        text: 'Every child learns differently. Every child deserves the opportunity to become a confident reader.'
      }
    ]
  },
  {
    slug: 'letters-but-cannot-read',
    title:
      'Why Some Children Can Recognise Letters But Still Struggle to Read',
    subtitle:
      'Knowing the alphabet is not the same as reading — and why pure letter sounds change everything.',
    excerpt:
      'Many children can recognise letters confidently, yet still struggle to read simple words. The gap between alphabet knowledge and real reading is more nuanced than most parents realise.',
    date: '2026-02-06',
    dateDisplay: '6 February 2026',
    readMinutes: 4,
    heroImage:
      'https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/m4zj0eji_image.png',
    heroAlt:
      'A young child kneeling on the floor matching letter and picture cards.',
    tags: ['Phonics', 'Montessori', 'Reading Development'],
    body: [
      {
        type: 'p',
        text: 'Many children can recognise letters confidently, yet still struggle to read simple words. Over the years, I have observed this repeatedly in classrooms.'
      },
      {
        type: 'p',
        text: 'A child may know the alphabet and even sing the alphabet song beautifully, yet when asked to read a simple word like cat or man, they suddenly become anxious and uncertain.'
      },
      {
        type: 'pullquote',
        text: 'Why does this happen? Isn\'t recognising letters the same as reading?'
      },
      {
        type: 'h2',
        text: 'Reading is a much deeper process'
      },
      { type: 'p', text: 'A child must learn to:' },
      {
        type: 'list',
        items: [
          'hear sounds clearly',
          'connect sounds to letters',
          'blend sounds smoothly',
          'process language in sequence',
          'develop confidence through repetition and success'
        ]
      },
      {
        type: 'p',
        text: 'As a Montessorian, I have always believed that how we teach is just as important as what we teach. Young children learn best when learning is:'
      },
      {
        type: 'list',
        items: ['hands-on', 'movement-based', 'multisensory', 'structured', 'meaningful']
      },
      {
        type: 'h2',
        text: 'The hidden problem: incorrect letter sounds'
      },
      {
        type: 'p',
        text: 'One important area that is often overlooked is the teaching of letter sounds. When children are taught sounds incorrectly with extra vowel additions such as "buh", "kuh", or "muh", blending becomes difficult and confusing.'
      },
      {
        type: 'p',
        text: 'For example, a child trying to read cat may hear: "kuh – a – tuh" instead of the pure sounds: "c – a – t".'
      },
      {
        type: 'p',
        text: 'This may seem small to adults, but for a young child learning to blend sounds, it makes a very big difference.'
      },
      {
        type: 'h2',
        text: 'They didn\'t need pressure — they needed a different approach'
      },
      {
        type: 'p',
        text: 'I have worked with many children who were labelled "weak readers" or "slow learners". They did not need more pressure. They just needed clearer instructions, sound awareness, movement, patience, and a different approach to learning.'
      },
      {
        type: 'p',
        text: 'When we understand child development and teach in a way that matches how children naturally learn, we often see confidence return very quickly.'
      },
      {
        type: 'pullquote',
        text: 'Sometimes a child is not struggling because they cannot learn. They may be struggling because we have not yet discovered the best way for them to learn.'
      }
    ]
  },
  {
    slug: 'movement-matters',
    title: 'Why Movement Matters in Early Childhood Learning',
    subtitle:
      'Movement is not getting in the way of learning. Movement IS part of learning.',
    excerpt:
      'Parents and teachers often ask, "Why can\'t my child sit still and focus?" Here\'s why that may be the wrong question to start with.',
    date: '2026-01-27',
    dateDisplay: '27 January 2026',
    readMinutes: 4,
    heroImage:
      'https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/ufyuzlco_image.png',
    heroAlt:
      'A child tracing the letter "s" in a Montessori sand tray while peers explore movement-based learning in the background.',
    tags: ['Montessori', 'Movement & Learning', 'Brain Gym'],
    body: [
      {
        type: 'p',
        text: 'One of the questions parents and teachers often ask is: "Why can\'t my child sit still and focus?"'
      },
      {
        type: 'p',
        text: 'It is a reasonable question. As adults, we sometimes forget that young children learn differently. While sitting and listening have their place, young children often learn best when they can move, explore, and engage actively with their environment.'
      },
      {
        type: 'pullquote',
        text: 'For children, movement is not separate from learning.'
      },
      {
        type: 'p',
        text: 'Young children learn by touching, carrying, sorting, matching, building, exploring, and interacting with their environment. Through these experiences, they develop coordination, concentration, confidence, and understanding.'
      },
      {
        type: 'h2',
        text: 'Why the Montessori approach values purposeful movement'
      },
      {
        type: 'p',
        text: 'This is one of the reasons I have always appreciated the Montessori approach to education. Children are given opportunities to learn through purposeful movement and hands-on experiences rather than through passive observation alone.'
      },
      {
        type: 'p',
        text: 'In my work with children — particularly those who struggled with reading and learning — I often noticed a positive change when lessons included movement and multisensory activities. Children who appeared distracted or reluctant became more engaged, more confident, and more willing to participate.'
      },
      {
        type: 'h2',
        text: 'What Brain Gym 101 taught me'
      },
      {
        type: 'p',
        text: 'My training in Brain Gym 101 further encouraged me to explore the connection between movement and learning. While every child is different, I have seen how simple movement-based activities can help some children become calmer, more focused, and better prepared for learning.'
      },
      {
        type: 'p',
        text: 'Something as simple as tracing letters, clapping syllables, moving while practising sounds, or working with hands-on materials can make learning more meaningful and enjoyable for a young child.'
      },
      {
        type: 'p',
        text: 'This does not mean children should never learn to sit quietly or develop self-discipline. Those skills are important and develop over time.'
      },
      {
        type: 'p',
        text: 'However, before asking why a child cannot sit still, it may be worth considering whether we are teaching in a way that matches how young children learn best.'
      },
      {
        type: 'p',
        text: 'When we understand child development and work with the child rather than against the child, learning often becomes more joyful, more engaging, and more successful.'
      },
      {
        type: 'pullquote',
        text: 'Perhaps movement is not getting in the way of learning. Movement IS part of learning.'
      }
    ]
  }
];

export const getArticleBySlug = (slug) =>
  articles.find((article) => article.slug === slug);
